import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  isInTable,
  selectedRect,
  moveTableRow,
  moveTableColumn,
} from '@tiptap/pm/tables';
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  ChevronsLeftRight,
  Merge,
  Split,
  Heading as HeadingIcon,
  Trash2,
  Rows3,
  Columns3,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react';
import { ColorPopover } from './ColorPopover';

/**
 * Floating menu anchored above the current table cell. Sits inside the
 * EditorPane <section> (NOT body-portalled) so EditorPane's auto-Done
 * click-out listener treats it as "inside the editor". Shares the
 * `data-popover-trigger` discipline so its buttons don't dismiss the menu
 * before their own onClick can run.
 *
 * Cell-bg + align buttons are NOT here — they land in Phase B alongside the
 * TableCellExtension that ships the `backgroundColor` / `align` attrs.
 */
export function TableBubbleMenu({ editor }: { editor: Editor }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const bgBtnRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [bgPopover, setBgPopover] = useState<DOMRect | null>(null);
  // Stable close callback so the popover's mousedown listener doesn't
  // unbind+rebind on every keystroke (this component re-renders per
  // selectionUpdate / transaction).
  const closeBgPopover = useCallback(() => setBgPopover(null), []);

  useEffect(() => {
    if (!editor) return;
    let raf = 0;
    // Track the last-anchored cell DOM. When the user types inside a cell,
    // selection updates fire on every keystroke but the cell DOM identity
    // doesn't change — so we can skip the `getBoundingClientRect()` reflow
    // and the setAnchor (preventing a parent re-render). Only reflow when
    // the cell changes (cursor moved between cells, table mutated, etc.).
    let lastCellEl: HTMLElement | null = null;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!editor.isEditable) {
          if (lastCellEl) lastCellEl = null;
          setAnchor(null);
          return;
        }
        const { state, view } = editor;
        if (!isInTable(state)) {
          if (lastCellEl) lastCellEl = null;
          setAnchor(null);
          return;
        }
        try {
          const fromDom = view.domAtPos(state.selection.from);
          const startNode =
            fromDom.node.nodeType === 1
              ? (fromDom.node as Element)
              : fromDom.node.parentElement;
          const cellEl = startNode?.closest?.('td, th') as HTMLElement | null;
          if (!cellEl) {
            if (lastCellEl) lastCellEl = null;
            setAnchor(null);
            return;
          }
          if (cellEl !== lastCellEl) {
            lastCellEl = cellEl;
            setAnchor(cellEl.getBoundingClientRect());
          }
          // else: same cell, skip the reflow.
        } catch {
          lastCellEl = null;
          setAnchor(null);
        }
      });
    };
    // selectionUpdate fires on cursor moves AND remote yjs applies that shift
    // the selection — covers the visibility cases. We deliberately do NOT
    // listen to `transaction`: keystrokes inside a cell fire transactions
    // per character, but the bubble menu's anchor cell hasn't moved. The
    // `lastCellEl` cache above would short-circuit those anyway, but
    // dropping the listener entirely saves the rAF schedule cost.
    editor.on('selectionUpdate', update);
    update();
    return () => {
      editor.off('selectionUpdate', update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [editor]);

  if (!anchor) return null;

  // Anchor the menu just above the active cell. Flip to below when near the
  // top of the viewport. Clamp `left` so the menu doesn't clip off the
  // right edge — uses an upper-bound estimate for menu width since we
  // don't measure the rendered DOM. The estimate is generous; on narrower
  // viewports the inner container's `flex-wrap` lets the strip reflow into
  // multiple rows so it still fits.
  const flipBelow = anchor.top < 60;
  const ESTIMATED_MENU_WIDTH = 320;
  const maxLeft = Math.max(8, window.innerWidth - ESTIMATED_MENU_WIDTH - 8);
  const style: React.CSSProperties = {
    position: 'fixed',
    top: flipBelow ? anchor.bottom + 6 : anchor.top - 6,
    left: Math.min(Math.max(8, anchor.left), maxLeft),
    transform: flipBelow ? undefined : 'translateY(-100%)',
    zIndex: 50,
    maxWidth: 'calc(100vw - 16px)',
  };

  function run(fn: (chain: ReturnType<Editor['chain']>) => unknown) {
    fn(editor.chain().focus());
  }

  // moveTableRow / moveTableColumn aren't exposed as TipTap chain commands —
  // dispatch them directly through the view. `selectedRect(state)` gives us
  // the row/col indices of the current selection. Wrapped in try/catch:
  // selectedRect throws if the cursor's no longer in a table (peer just
  // deleted it). We no-op on error; the menu's anchor effect will hide it
  // on the next transaction.
  function moveRow(delta: -1 | 1) {
    try {
      const rect = selectedRect(editor.state);
      if (!rect) return;
      const from = rect.top;
      const to = Math.max(0, Math.min(rect.map.height - 1, from + delta));
      if (to === from) return;
      const cmd = moveTableRow({ from, to });
      cmd(editor.state, editor.view.dispatch);
      editor.view.focus();
    } catch {
      /* peer deleted the table — no-op */
    }
  }

  function moveCol(delta: -1 | 1) {
    try {
      const rect = selectedRect(editor.state);
      if (!rect) return;
      const from = rect.left;
      const to = Math.max(0, Math.min(rect.map.width - 1, from + delta));
      if (to === from) return;
      const cmd = moveTableColumn({ from, to });
      cmd(editor.state, editor.view.dispatch);
      editor.view.focus();
    } catch {
      /* peer deleted the table — no-op */
    }
  }

  // Compute current cell alignment once per render — avoids three identical
  // PM-walk lookups inline in the JSX below (one per align button).
  const cellAlign =
    editor.getAttributes('tableCell')?.align ??
    editor.getAttributes('tableHeader')?.align ??
    null;

  return (
    <>
      {bgPopover && (
        <ColorPopover
          editor={editor}
          mode="cell-bg"
          onClose={closeBgPopover}
          anchor={bgPopover}
        />
      )}
    <div
      ref={ref}
      style={style}
      data-popover-trigger
      className="rounded-md border border-ink-700 bg-ink-900 shadow-lg p-1"
    >
      <div className="flex flex-wrap items-center gap-0.5">
        <BubbleBtn
          onClick={() => run((c) => c.addRowBefore().run())}
          title="Insert row above"
          disabled={!editor.can().addRowBefore()}
        >
          <ChevronUp size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.addRowAfter().run())}
          title="Insert row below"
          disabled={!editor.can().addRowAfter()}
        >
          <ChevronDown size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.addColumnBefore().run())}
          title="Insert column left"
          disabled={!editor.can().addColumnBefore()}
        >
          <ChevronsLeftRight size={13} className="-rotate-90" />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.addColumnAfter().run())}
          title="Insert column right"
          disabled={!editor.can().addColumnAfter()}
        >
          <ChevronsLeftRight size={13} className="rotate-90" />
        </BubbleBtn>
        <Sep />
        <BubbleBtn
          onClick={() => run((c) => c.toggleHeaderRow().run())}
          title="Toggle header row"
          active={editor.isActive('tableHeader')}
        >
          <HeadingIcon size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.mergeCells().run())}
          title="Merge selected cells"
          disabled={!editor.can().mergeCells()}
        >
          <Merge size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.splitCell().run())}
          title="Split cell"
          disabled={!editor.can().splitCell()}
        >
          <Split size={13} />
        </BubbleBtn>
        <Sep />
        <BubbleBtn onClick={() => moveRow(-1)} title="Move row up">
          <ArrowUp size={13} />
        </BubbleBtn>
        <BubbleBtn onClick={() => moveRow(1)} title="Move row down">
          <ArrowDown size={13} />
        </BubbleBtn>
        <BubbleBtn onClick={() => moveCol(-1)} title="Move column left">
          <ArrowLeft size={13} />
        </BubbleBtn>
        <BubbleBtn onClick={() => moveCol(1)} title="Move column right">
          <ArrowRight size={13} />
        </BubbleBtn>
        <Sep />
        <button
          type="button"
          ref={bgBtnRef}
          data-popover-trigger
          onClick={() =>
            setBgPopover((open) =>
              open ? null : bgBtnRef.current?.getBoundingClientRect() ?? null,
            )
          }
          title="Cell background colour"
          className={`flex items-center px-1.5 py-1 rounded text-ink-200 hover:bg-ink-800 ${
            bgPopover ? 'bg-ink-800 text-white' : ''
          }`}
        >
          <Palette size={13} />
        </button>
        <BubbleBtn
          onClick={() => (editor.commands as any).setCellAlign('left')}
          title="Align cell left"
          active={cellAlign === 'left'}
        >
          <AlignLeft size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => (editor.commands as any).setCellAlign('center')}
          title="Align cell centre"
          active={cellAlign === 'center'}
        >
          <AlignCenter size={13} />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => (editor.commands as any).setCellAlign('right')}
          title="Align cell right"
          active={cellAlign === 'right'}
        >
          <AlignRight size={13} />
        </BubbleBtn>
        <Sep />
        <BubbleBtn
          onClick={() => run((c) => c.deleteRow().run())}
          title="Delete row"
          disabled={!editor.can().deleteRow()}
        >
          <Rows3 size={13} className="text-red-400" />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.deleteColumn().run())}
          title="Delete column"
          disabled={!editor.can().deleteColumn()}
        >
          <Columns3 size={13} className="text-red-400" />
        </BubbleBtn>
        <BubbleBtn
          onClick={() => run((c) => c.deleteTable().run())}
          title="Delete table"
          disabled={!editor.can().deleteTable()}
        >
          <Trash2 size={13} className="text-red-400" />
        </BubbleBtn>
      </div>
    </div>
    </>
  );
}

function Sep() {
  return <span className="mx-0.5 h-4 w-px bg-ink-700" />;
}

function BubbleBtn({
  children,
  active,
  ...rest
}: {
  children: React.ReactNode;
  active?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={`flex items-center px-1.5 py-1 rounded text-ink-200 hover:bg-ink-800 disabled:text-ink-600 disabled:hover:bg-transparent disabled:cursor-not-allowed ${
        active ? 'bg-ink-800 text-white' : ''
      }`}
    >
      {children}
    </button>
  );
}
